/**
 * Tiny audio helper for the inbound-SMS chime.
 *
 * Uses the Web Audio API to synthesize the chime on the fly so we don't
 * have to ship any audio assets. Members can pick from a small library
 * of tones and preview them before saving. Failures are swallowed
 * silently — the chime is a courtesy notification, never critical.
 */

export const SMS_CHIME_STORAGE_KEY = 'sms_chime_enabled';
export const SMS_CHIME_TONE_STORAGE_KEY = 'sms_chime_tone';
export const SMS_CHIME_VOLUME_STORAGE_KEY = 'sms_chime_volume';
export const SMS_CHIME_DND_ENABLED_KEY = 'sms_chime_dnd_enabled';
export const SMS_CHIME_DND_START_KEY = 'sms_chime_dnd_start';
export const SMS_CHIME_DND_END_KEY = 'sms_chime_dnd_end';

export const DND_DEFAULT_START = '22:00';
export const DND_DEFAULT_END = '07:00';

/** Available chime volume levels. Values are peak gain values fed into
 *  the exponential ramp inside ``playSmsChime``. Anything above ~0.4 gets
 *  uncomfortably loud on most laptops so we cap at 0.35. */
export const SMS_CHIME_VOLUMES = {
  soft:   { label: 'Soft',   gain: 0.08 },
  medium: { label: 'Medium', gain: 0.18 },
  loud:   { label: 'Loud',   gain: 0.35 },
};
export const DEFAULT_VOLUME_KEY = 'medium';

/**
 * Chime library. Each entry is a list of notes played in order.
 * ``freq`` in Hz, ``start`` and ``dur`` in seconds, ``type`` is any
 * OscillatorType the browser supports.
 */
export const SMS_CHIME_TONES = {
  classic: {
    label: 'Classic Ding',
    hint: 'Two-tone A5 → E6, gentle sine',
    notes: [
      { freq: 880, start: 0, dur: 0.18, type: 'sine' },
      { freq: 1320, start: 0.14, dur: 0.22, type: 'sine' },
    ],
  },
  soft: {
    label: 'Soft Bell',
    hint: 'Long triangle wave, calm',
    notes: [
      { freq: 660, start: 0, dur: 0.35, type: 'triangle' },
      { freq: 990, start: 0.10, dur: 0.35, type: 'triangle' },
    ],
  },
  pop: {
    label: 'Pop',
    hint: 'Snappy square wave blip',
    notes: [
      { freq: 1200, start: 0, dur: 0.08, type: 'square' },
      { freq: 1600, start: 0.09, dur: 0.10, type: 'square' },
    ],
  },
  chirp: {
    label: 'Chirp',
    hint: 'Three ascending notes',
    notes: [
      { freq: 700, start: 0, dur: 0.10, type: 'sine' },
      { freq: 1050, start: 0.08, dur: 0.10, type: 'sine' },
      { freq: 1500, start: 0.16, dur: 0.14, type: 'sine' },
    ],
  },
};

export const DEFAULT_TONE_KEY = 'classic';

let audioCtx = null;

function getContext() {
  if (audioCtx) return audioCtx;
  try {
    const Ctor = window.AudioContext || window.webkitAudioContext;
    if (!Ctor) return null;
    audioCtx = new Ctor();
    return audioCtx;
  } catch {
    return null;
  }
}

/** Is the SMS chime enabled by the current user? */
export function isSmsChimeEnabled() {
  try {
    return localStorage.getItem(SMS_CHIME_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

/** Return true if the current local time falls inside the caller's
 *  Do-Not-Disturb window (if any). Window wraps midnight when
 *  start > end (e.g. 22:00 → 07:00). Storage values are HH:MM strings. */
export function isInDndWindow(now = new Date()) {
  try {
    if (localStorage.getItem(SMS_CHIME_DND_ENABLED_KEY) !== '1') return false;
    const start = localStorage.getItem(SMS_CHIME_DND_START_KEY) || DND_DEFAULT_START;
    const end = localStorage.getItem(SMS_CHIME_DND_END_KEY) || DND_DEFAULT_END;
    if (!start || !end || start === end) return false;
    const hh = String(now.getHours()).padStart(2, '0');
    const mm = String(now.getMinutes()).padStart(2, '0');
    const cur = `${hh}:${mm}`;
    if (start < end) return start <= cur && cur < end;
    // Wraps midnight
    return cur >= start || cur < end;
  } catch {
    return false;
  }
}

/** Convenience for the websocket handler — true only when the chime is
 *  both enabled AND we're outside any active Do-Not-Disturb window. */
export function isSmsChimeAudibleNow() {
  return isSmsChimeEnabled() && !isInDndWindow();
}

/** Get the current chime tone key, falling back to the default. */
export function getSmsChimeTone() {
  try {
    const t = localStorage.getItem(SMS_CHIME_TONE_STORAGE_KEY);
    return SMS_CHIME_TONES[t] ? t : DEFAULT_TONE_KEY;
  } catch {
    return DEFAULT_TONE_KEY;
  }
}

/** Get the current chime volume key, falling back to the default. */
export function getSmsChimeVolume() {
  try {
    const v = localStorage.getItem(SMS_CHIME_VOLUME_STORAGE_KEY);
    return SMS_CHIME_VOLUMES[v] ? v : DEFAULT_VOLUME_KEY;
  } catch {
    return DEFAULT_VOLUME_KEY;
  }
}

/**
 * Play the currently-selected chime (or a specific ``toneKey`` /
 * ``volumeKey`` when the caller wants to preview a tone or a volume
 * before saving). Silently no-ops if the Web Audio API is unavailable.
 */
export function playSmsChime(toneKey, volumeKey) {
  const tKey = toneKey && SMS_CHIME_TONES[toneKey] ? toneKey : getSmsChimeTone();
  const vKey = volumeKey && SMS_CHIME_VOLUMES[volumeKey] ? volumeKey : getSmsChimeVolume();
  const tone = SMS_CHIME_TONES[tKey] || SMS_CHIME_TONES[DEFAULT_TONE_KEY];
  const peakGain = (SMS_CHIME_VOLUMES[vKey] || SMS_CHIME_VOLUMES[DEFAULT_VOLUME_KEY]).gain;

  const ctx = getContext();
  if (!ctx) return;
  if (ctx.state === 'suspended') {
    ctx.resume().catch(() => {});
  }
  const now = ctx.currentTime;
  tone.notes.forEach(({ freq, start, dur, type }) => {
    try {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = type || 'sine';
      osc.frequency.value = freq;
      // Quick attack, gentle release so it feels like a message notification.
      gain.gain.setValueAtTime(0.0001, now + start);
      gain.gain.exponentialRampToValueAtTime(peakGain, now + start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + start);
      osc.stop(now + start + dur + 0.02);
    } catch {
      /* ignore */
    }
  });
}
